import { useState } from 'react';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import { Loading, ErrorState } from '../components/States';
import Icon from '../components/Icon';
import { ICONS } from '../lib/icons';

export default function QemuServers() {
  const { data, loading, error, reload } = useAsync(() => api.getQemuServers(), []);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({ region: '', name: '', host: '', port: 16509, uri: '', privateKey: '' });
  const [editing, setEditing] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.createQemuServer(formData);
      setFormData({ region: '', name: '', host: '', port: 16509, uri: '', privateKey: '' });
      setCreating(false);
      reload();
    } catch (err) {
      console.error('Failed to create server:', err);
      alert('Error: ' + err.message);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.updateQemuServer(editing.id, formData);
      setFormData({ region: '', name: '', host: '', port: 16509, uri: '', privateKey: '' });
      setEditing(null);
      reload();
    } catch (err) {
      console.error('Failed to update server:', err);
      alert('Error: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this QEMU server?')) return;
    try {
      await api.deleteQemuServer(id);
      reload();
    } catch (err) {
      console.error('Failed to delete server:', err);
      alert('Error: ' + err.message);
    }
  };

  const handleSync = async (id, serverName) => {
    setActionLoading(`sync-${id}`);
    try {
      await api.syncQemuServerResources(id);
      alert(`✓ Sync triggered for ${serverName}`);
      reload();
    } catch (err) {
      console.error('Failed to sync server:', err);
      alert('Sync error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleCompute = async (id, serverName) => {
    setActionLoading(`compute-${id}`);
    try {
      await api.computeQemuServerResources(id);
      alert(`✓ Compute triggered for ${serverName}`);
      reload();
    } catch (err) {
      console.error('Failed to compute resources:', err);
      alert('Compute error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return <Loading label="Loading QEMU servers…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const servers = data || [];
  const regions = [...new Set(servers.map(s => s.region))];
  const filtered = selectedRegion ? servers.filter(s => s.region === selectedRegion) : servers;

  return (
    <div className="stack">
      <PageHeader
        title="QEMU Servers"
        subtitle="Register and manage KVM/QEMU hypervisor servers for each region."
      />

      {/* Create Server Form */}
      {creating && !editing && (
        <Card title="Register New Server">
          <form onSubmit={handleCreate} className="form-stack">
            <div className="form-row">
              <input
                type="text"
                placeholder="Region (e.g., us-east-1)"
                value={formData.region}
                onChange={e => setFormData({...formData, region: e.target.value})}
                required
              />
              <input
                type="text"
                placeholder="Server Name"
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div className="form-row">
              <input
                type="text"
                placeholder="Host (IP or hostname)"
                value={formData.host}
                onChange={e => setFormData({...formData, host: e.target.value})}
                required
              />
              <input
                type="number"
                placeholder="Port"
                value={formData.port}
                onChange={e => setFormData({...formData, port: parseInt(e.target.value)})}
                min="1"
                max="65535"
                required
              />
            </div>
            <input
              type="text"
              placeholder="URI (optional, auto-generated if blank)"
              value={formData.uri}
              onChange={e => setFormData({...formData, uri: e.target.value})}
            />
            <textarea
              placeholder="Private Key (optional, for authentication)"
              value={formData.privateKey}
              onChange={e => setFormData({...formData, privateKey: e.target.value})}
              style={{ minHeight: '120px' }}
            />
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Create Server</button>
              <button type="button" className="btn btn-secondary" onClick={() => setCreating(false)}>Cancel</button>
            </div>
          </form>
        </Card>
      )}

      {/* Edit Server Form */}
      {editing && (
        <Card title={`Edit ${editing.name}`}>
          <form onSubmit={handleUpdate} className="form-stack">
            <div className="form-row">
              <input
                type="text"
                placeholder="Region"
                value={formData.region}
                onChange={e => setFormData({...formData, region: e.target.value})}
                required
              />
              <input
                type="text"
                placeholder="Server Name"
                value={formData.name}
                onChange={e => setFormData({...formData, name: e.target.value})}
                required
              />
            </div>
            <div className="form-row">
              <input
                type="text"
                placeholder="Host"
                value={formData.host}
                onChange={e => setFormData({...formData, host: e.target.value})}
                required
              />
              <input
                type="number"
                placeholder="Port"
                value={formData.port}
                onChange={e => setFormData({...formData, port: parseInt(e.target.value)})}
                required
              />
            </div>
            <input
              type="text"
              placeholder="URI"
              value={formData.uri}
              onChange={e => setFormData({...formData, uri: e.target.value})}
            />
            <textarea
              placeholder="Private Key (optional, for authentication)"
              value={formData.privateKey}
              onChange={e => setFormData({...formData, privateKey: e.target.value})}
              style={{ minHeight: '120px' }}
            />
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Save Changes</button>
              <button type="button" className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
            </div>
          </form>
        </Card>
      )}

      {/* Region Filter */}
      <div className="filter-bar">
        <select
          value={selectedRegion}
          onChange={e => setSelectedRegion(e.target.value)}
          className="select-sm"
        >
          <option value="">All Regions</option>
          {regions.map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        {!creating && !editing && (
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Icon name={ICONS.plus} /> Register Server
          </button>
        )}
      </div>

      {/* Servers Table */}
      <Card title={selectedRegion ? `Region: ${selectedRegion}` : 'All Servers'}>
        {filtered.length === 0 ? (
          <p className="text-muted">No QEMU servers registered.</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Region</th>
                  <th>Host</th>
                  <th>Port</th>
                  <th>Status</th>
                  <th>Last Check</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(srv => (
                  <tr key={srv.id}>
                    <td className="font-mono">{srv.name}</td>
                    <td>{srv.region}</td>
                    <td className="font-mono">{srv.host}</td>
                    <td className="font-mono">{srv.port}</td>
                    <td>
                      <span className={`status-badge status-${srv.status}`}>
                        {srv.status}
                      </span>
                    </td>
                    <td className="text-muted text-sm">
                      {srv.lastCheck ? new Date(srv.lastCheck).toLocaleDateString() : 'Never'}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          onClick={() => handleSync(srv.id, srv.name)}
                          disabled={actionLoading === `sync-${srv.id}`}
                          className="btn-icon"
                          title="Sync resources"
                        >
                          {actionLoading === `sync-${srv.id}` ? '⟳' : <Icon name={ICONS.sync || 'refresh'} />}
                        </button>
                        <button
                          onClick={() => handleCompute(srv.id, srv.name)}
                          disabled={actionLoading === `compute-${srv.id}`}
                          className="btn-icon"
                          title="Compute metrics"
                        >
                          {actionLoading === `compute-${srv.id}` ? '⟳' : '⚙'}
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await api.checkQemuServer(srv.id);
                              reload();
                            } catch (err) {
                              console.error('Failed to check server:', err);
                              alert('Check error: ' + err.message);
                            }
                          }}
                          className="btn-icon"
                          title="Check server health"
                        >
                          <Icon name={ICONS.refresh} />
                        </button>
                        <button
                          onClick={() => {
                            setEditing(srv);
                            setFormData({
                              region: srv.region,
                              name: srv.name,
                              host: srv.host,
                              port: srv.port,
                              uri: srv.uri,
                              privateKey: srv.privateKey,
                            });
                          }}
                          className="btn-icon"
                          title="Edit"
                        >
                          <Icon name={ICONS.edit} />
                        </button>
                        <button
                          onClick={() => handleDelete(srv.id)}
                          className="btn-icon btn-icon--danger"
                          title="Delete"
                        >
                          <Icon name={ICONS.delete} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
